/**
 * GRIDHEIMR — Hatchery Lab Page
 *
 * Public demo page for hatching AI personas with unique personality profiles.
 */

import React from "react";
import { useHatchery } from "../hooks/useHatchery";
import { HatchButton } from "../components/HatchButton";
import { PersonaGallery } from "../components/PersonaGallery";
import { BLUE, YELLOW, PIXEL, MONO } from "../theme";

/**
 * Hatchery Lab Page Component
 *
 * Public-facing demo page that allows users to:
 * - Generate AI personas with randomized MBTI personality profiles
 * - Browse recently hatched personas in a gallery
 * - View persona details including appearance, voice, psychology, and strengths
 */
export function HatcheryLabPage() {
  const { hatchAgent, hatching } = useHatchery(50, 10000);

  return (
    <div style={styles.container}>
      {/* Hero Section */}
      <div style={styles.hero}>
        <div style={styles.heroIcon}>🧪✨</div>
        <h1 style={styles.heroTitle}>Hatchery Lab</h1>
        <p style={styles.heroSubtitle}>
          Welcome to the GRIDHEIMR Hatchery Lab — where AI personas come to life!
        </p>
        <p style={styles.heroDescription}>
          Each "hatch" generates a unique AI agent with a randomized MBTI personality profile,
          complete with appearance, voice characteristics, psychological traits, and narrative strengths.
          Experiment, explore, and discover what emerges from the digital egg.
        </p>
      </div>

      {/* Hatch Button Section */}
      <div style={styles.hatchSection}>
        <HatchButton onHatch={hatchAgent} loading={hatching} />
      </div>

      {/* Divider */}
      <div style={styles.divider}></div>

      {/* Gallery Section */}
      <div style={styles.gallerySection}>
        <PersonaGallery />
      </div>

      {/* Footer Info */}
      <div style={styles.footer}>
        <p style={styles.footerText}>
          🔬 <strong>Experiment Status:</strong> This is a live demo of GRIDHEIMR's agent generation system.
        </p>
        <p style={styles.footerText}>
          Each persona is uniquely generated and stored in our gallery for exploration.
        </p>
      </div>
    </div>
  );
}

/**
 * Inline styles
 */
const styles: Record<string, React.CSSProperties> = {
  container: {
    width: "100%",
    maxWidth: "1400px",
    margin: "0 auto",
    padding: "2rem",
    minHeight: "100vh",
  },
  hero: {
    textAlign: "center",
    marginBottom: "3rem",
    padding: "3rem 1rem",
    background: `rgba(0,106,167,0.06)`,
    border: `1px solid rgba(0,106,167,0.2)`,
    boxShadow: `0 0 30px rgba(0,106,167,0.08)`,
  },
  heroIcon: {
    fontSize: "5rem",
    marginBottom: "1rem",
    display: "inline-block",
    filter: `drop-shadow(0 4px 8px rgba(0,106,167,0.3))`,
  },
  heroTitle: {
    fontFamily: PIXEL,
    fontSize: "clamp(1rem, 3vw, 1.6rem)",
    margin: "0 0 1rem 0",
    color: BLUE,
    fontWeight: 400,
    letterSpacing: "0.06em",
    textShadow: `0 0 20px rgba(0,106,167,0.5)`,
  },
  heroSubtitle: {
    fontFamily: MONO,
    fontSize: "1rem",
    margin: "0 0 1.5rem 0",
    color: "rgba(255,255,255,0.7)",
    fontWeight: 600,
    letterSpacing: "0.05em",
  },
  heroDescription: {
    fontFamily: MONO,
    fontSize: "0.9rem",
    margin: "0 auto",
    maxWidth: "750px",
    color: "rgba(255,255,255,0.5)",
    lineHeight: 1.8,
  },
  hatchSection: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "4rem",
    padding: "1rem",
  },
  divider: {
    height: "2px",
    background: `linear-gradient(90deg, transparent, ${BLUE}, ${YELLOW}, transparent)`,
    margin: "4rem 0",
    boxShadow: `0 0 12px rgba(0,106,167,0.3)`,
  },
  gallerySection: {
    marginBottom: "4rem",
  },
  footer: {
    textAlign: "center",
    padding: "2.5rem 2rem",
    background: `rgba(0,106,167,0.04)`,
    border: `1px solid rgba(0,106,167,0.15)`,
    boxShadow: `0 0 20px rgba(0,106,167,0.05)`,
  },
  footerText: {
    margin: "0.75rem 0",
    fontFamily: MONO,
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.5)",
    lineHeight: 1.7,
  },
};
